import { ExpressContactKind } from "../../types/express-master.type";
import {
  ExpressDocType,
  ExpressDocumentStatus,
  ExpressPriceType,
} from "../../types/express-document.type";
import { EXPRESS_PATH } from "../ExpressData/constants";

/** How a type keys its lines: qty × unit price (R5 / P4), an amount (O1), or credit bills (E1). */
export type LineMode = "QTY_PRICE" | "AMOUNT" | "BILLS";

/** Which tax report a type feeds: ภาษีซื้อ (R5 / P4) or ภาษีขาย (O1). */
export type TaxReportKind = "PURCHASE" | "SALES";

/**
 * What sets one entry screen apart from the others. The four types share one form; everything
 * a type adds or leaves out is read from here, in the words the Express screen uses.
 */
export interface ExpressDocTypeConfig {
  docType: ExpressDocType;
  menuTitle: string;
  path: string;
  /** The Express contacts the party is picked from; null on E1, whose party is a GDP customer. */
  contactKind: ExpressContactKind | null;
  partyLabel: string;
  docNoLabel: string;
  docDateLabel: string;
  remarkLabel: string;
  lineMode: LineMode;
  /** เลขที่บิล, ลวท. and ยื่นงวด of the supplier's tax invoice. */
  hasSupplierBill: boolean;
  /** เครดิต and ครบกำหนด. */
  hasCreditTerm: boolean;
  hasDiscount: boolean;
  /** เงินสด, ชำระโดยอื่นๆ and ภาษี ณ ที่จ่าย. */
  hasPayment: boolean;
  taxReport: TaxReportKind | null;
  /** The list's amount: what the document totals, or for a receipt what it took in. */
  listAmountField: "net_amount" | "received_amount";
}

export const EXPRESS_DOC_TYPES: ExpressDocType[] = ["R5", "P4", "O1", "E1"];

export const EXPRESS_DOC_TYPE_CONFIG: Record<ExpressDocType, ExpressDocTypeConfig> = {
  R5: {
    docType: "R5",
    menuTitle: "ซื้อเชื่อ",
    path: `${EXPRESS_PATH}/credit-purchases`,
    contactKind: "SUPPLIER",
    partyLabel: "ผู้จำหน่าย",
    docNoLabel: "เลขที่ใบรับสินค้า",
    docDateLabel: "วันที่",
    remarkLabel: "หมายเหตุ",
    lineMode: "QTY_PRICE",
    hasSupplierBill: true,
    hasCreditTerm: true,
    hasDiscount: true,
    hasPayment: false,
    taxReport: "PURCHASE",
    listAmountField: "net_amount",
  },
  P4: {
    docType: "P4",
    menuTitle: "ซื้อสด",
    path: `${EXPRESS_PATH}/cash-purchases`,
    contactKind: "SUPPLIER",
    partyLabel: "ผู้จำหน่าย",
    docNoLabel: "เลขที่ใบซื้อสด",
    docDateLabel: "วันที่",
    remarkLabel: "หมายเหตุ",
    lineMode: "QTY_PRICE",
    hasSupplierBill: true,
    hasCreditTerm: false,
    hasDiscount: true,
    hasPayment: false,
    taxReport: "PURCHASE",
    listAmountField: "net_amount",
  },
  O1: {
    docType: "O1",
    menuTitle: "รายได้อื่นๆ",
    path: `${EXPRESS_PATH}/other-incomes`,
    contactKind: "CUSTOMER",
    partyLabel: "รหัสลูกค้า",
    docNoLabel: "เลขที่เอกสาร",
    docDateLabel: "วันที่",
    remarkLabel: "คำอธิบาย",
    lineMode: "AMOUNT",
    hasSupplierBill: false,
    hasCreditTerm: false,
    hasDiscount: false,
    hasPayment: true,
    taxReport: "SALES",
    listAmountField: "net_amount",
  },
  E1: {
    docType: "E1",
    menuTitle: "รับชำระหนี้",
    path: `${EXPRESS_PATH}/debt-receipts`,
    contactKind: null,
    partyLabel: "รหัสลูกค้า",
    docNoLabel: "เลขที่ใบเสร็จ",
    docDateLabel: "วันที่ใบเสร็จ",
    remarkLabel: "หมายเหตุ",
    lineMode: "BILLS",
    hasSupplierBill: false,
    hasCreditTerm: false,
    hasDiscount: false,
    hasPayment: true,
    taxReport: null,
    listAmountField: "received_amount",
  },
};

export const documentPageTitle = (config: ExpressDocTypeConfig) =>
  `${config.menuTitle} (${config.docType})`;

export const documentNewPath = (config: ExpressDocTypeConfig) => `${config.path}/new`;

export const documentDetailPath = (config: ExpressDocTypeConfig, uuid: string) =>
  `${config.path}/${encodeURIComponent(uuid)}`;

/** The price types apply to every type that keys lines; a receipt pays bills that carry their own. */
export const hasPriceType = (config: ExpressDocTypeConfig) => config.lineMode !== "BILLS";

export const LINES_TAB_LABEL = "รายการสินค้า";
export const BILL_LINES_TAB_LABEL = "รายการบิลที่รับชำระ";

export const TAX_REPORT_TAB_LABEL: Record<TaxReportKind, string> = {
  PURCHASE: "รายการภาษีซื้อ",
  SALES: "รายการภาษีขาย",
};

/** The tax report's party column: who sold to the branch, or who the branch sold to. */
export const TAX_REPORT_PARTY_LABEL: Record<TaxReportKind, string> = {
  PURCHASE: "ชื่อผู้ขายสินค้า/ผู้ให้บริการ",
  SALES: "ชื่อผู้ซื้อสินค้า/ผู้รับบริการ",
};

export const PRICE_TYPE_OPTIONS: { value: ExpressPriceType; label: string }[] = [
  { value: 0, label: "0 - ไม่มี VAT" },
  { value: 1, label: "1 - รวม VAT" },
  { value: 2, label: "2 - แยก VAT" },
];

export const NO_VAT_PRICE_TYPE: ExpressPriceType = 0;

/** The price type a new document starts on; the keyer switches it to match the bill. */
export const DEFAULT_PRICE_TYPE: ExpressPriceType = NO_VAT_PRICE_TYPE;

type ChipColor = "success" | "error";

export const DOCUMENT_STATUS_LABEL: Record<ExpressDocumentStatus, string> = {
  ACTIVE: "ใช้งาน",
  CANCELLED: "ยกเลิก",
};

export const DOCUMENT_STATUS_COLOR: Record<ExpressDocumentStatus, ChipColor> = {
  ACTIVE: "success",
  CANCELLED: "error",
};

/** Longest remark, reference and cancel reason the server stores. */
export const DOCUMENT_TEXT_MAX_LENGTH = 255;

/** Longest supplier bill number and salesperson code the form accepts. */
export const DOCUMENT_CODE_MAX_LENGTH = 20;

/** คลัง is a short Express warehouse code, e.g. 031–039 for a route. */
export const WAREHOUSE_CODE_MAX_LENGTH = 10;

/** The longest `search` the list sends. */
export const DOCUMENT_SEARCH_MAX_LENGTH = 100;

/** Decimal places a quantity and a unit price keep (the server's DECIMAL(18, 4)). */
export const QTY_DECIMALS = 4;
export const UNIT_PRICE_DECIMALS = 4;
/** Decimal places of every amount of money. */
export const MONEY_DECIMALS = 2;
